import dayjs from '~/timeInit';

import { formatDateShortDD } from '~/services/TimeService';

export type RecordLike = Record<string, unknown>;

export function getLatestSyncedAt(
  ...syncedAtValues: Array<string | null | undefined | Array<string | null | undefined>>
): string | undefined {
  const timestamps = syncedAtValues
    .flat()
    .filter((value): value is string => !!value)
    .map((value) => dayjs(value))
    .filter((value) => value.isValid());

  if (timestamps.length === 0) {
    return undefined;
  }

  return dayjs.max(timestamps)?.toISOString();
}

export type ChecklistStatus = 'approved' | 'requires approval' | 'blocked';
export type MarginBand = 'red' | 'yellow' | 'green';
export type ChecklistDetail = {
  label: string;
  value: string;
  expandableItems?: Array<string>;
};

export function getStatusLabel(status: ChecklistStatus) {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'requires approval':
      return 'Requires approval';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Unknown';
  }
}

export function formatOptionalNumber(value?: number) {
  return isFiniteNumber(value) ? String(value) : 'N/A';
}

export function formatMoney(value?: number | null, currencyCode = 'AUD') {
  return isFiniteNumber(value) ? `${currencyCode} ${value}` : 'N/A';
}

export function formatPrice(value: string | number | null | undefined, currencyCode = 'AUD') {
  if (value == null || value === '') {
    return '—';
  }
  return `${currencyCode} ${value}`;
}

export function formatContextDate(value?: string) {
  return value ? formatDateShortDD(value) : 'Not available';
}

export function formatBooleanDataPointValue({
  dataAvailable,
  enabled,
  unavailableValue = 'N/A',
}: {
  dataAvailable: boolean;
  enabled?: boolean;
  unavailableValue?: string;
}) {
  if (!dataAvailable) {
    return unavailableValue;
  }
  return enabled ? 'Yes' : 'No';
}

export function formatLoadedDataPointValue({ dataAvailable, loaded }: { dataAvailable: boolean; loaded: boolean }) {
  if (!dataAvailable) {
    return 'N/A';
  }
  return loaded ? 'Loaded' : 'Not loaded';
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function normalizeString(value?: string | null) {
  return value?.trim() || undefined;
}

export function normalizeComparisonText(value: string) {
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

export function readString(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const fieldValue = record[field];

    if (typeof fieldValue === 'string' && fieldValue.trim()) {
      return fieldValue;
    }
  }

  return undefined;
}

export function readNumber(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const fieldValue = record[field];
    const numberValue = typeof fieldValue === 'string' && fieldValue.trim() !== '' ? Number(fieldValue) : fieldValue;

    if (isFiniteNumber(numberValue)) {
      return numberValue;
    }
  }

  return undefined;
}

export function readBoolean(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const value = record[field];

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') {
        return true;
      }

      if (value.toLowerCase() === 'false') {
        return false;
      }
    }
  }

  return undefined;
}

export function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}

export function getPackageDisplayName(offerPackage: App.Package) {
  return (
    offerPackage.deal_option_name || offerPackage.name || String(offerPackage.id_salesforce_external || 'Deal option')
  );
}

// Deal option names come through as e.g. "5n Duplex w/ Pool 6A" — capitalize the nights
// abbreviation for display ("5N Duplex...").
export function formatDealOptionName(name: string) {
  return name.replace(/^(\d+)n\b/i, '$1N');
}

export function mapOpportunityDealsByPackageId<Deal>(opportunityDeals?: Array<Deal>) {
  return new Map(
    (opportunityDeals ?? [])
      .map((deal) => {
        const dealId = readString(deal as unknown as RecordLike, ['id', 'sfid']);

        return dealId ? ([dealId, deal] as const) : undefined;
      })
      .filter((dealEntry): dealEntry is readonly [string, Deal] => !!dealEntry),
  );
}

export function getDealToggleLabel({ dealCount, dealsExpanded }: { dealCount: number; dealsExpanded: boolean }) {
  const dealNoun = dealCount === 1 ? 'deal option' : 'deal options';
  return `${dealsExpanded ? 'Hide' : 'View'} ${dealCount} ${dealNoun}`;
}
